import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { jwtDecode } from "jwt-decode";
import MensajeValidacion from "../components/MensajeValidacion";

const SalidaLote = () => {
  const [materiales, setMateriales] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [usuario, setUsuario] = useState(null);
  
  const [proyectoId, setProyectoId] = useState("");
  const [almacenId, setAlmacenId] = useState("");
  const [departamentoId, setDepartamentoId] = useState("");
  const [responsable, setResponsable] = useState("");
  
  // ✔ FECHA LOCAL CORREGIDA (NO UTC)
  const obtenerFechaLocal = () => {
    const hoy = new Date();
    return new Date(hoy.getTime() - hoy.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
  };
  
  const [fecha, setFecha] = useState(obtenerFechaLocal());
  const [observacion, setObservacion] = useState("");
  
  // Array de productos para el lote
  const [productos, setProductos] = useState([]);
  
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "", detalle: "" });
  const [guardando, setGuardando] = useState(false);

  // 🔐 Obtener usuario del token
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUsuario(decoded);
        const nombreUsuario = decoded.nombre || decoded.username || decoded.email || "Usuario";
        setResponsable(nombreUsuario);
      } catch (error) {
        setResponsable("Usuario");
      }
    }
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [resMateriales, resCategorias, resAlmacenes, resDepartamentos, resProyectos] = await Promise.all([
        api.get("/materiales"),
        api.get("/categorias"),
        api.get("/almacenes"),
        api.get("/departamentos"),
        api.get("/proyectos/activos")
      ]);

      setMateriales(resMateriales.data);
      setCategorias(resCategorias.data);
      setAlmacenes(resAlmacenes.data);
      setDepartamentos(resDepartamentos.data);
      setProyectos(resProyectos.data);
    } catch (error) {
      console.error("Error cargando datos:", error);
      setMensaje({ texto: "❌ Error al cargar datos", tipo: "error" });
    }
  };

  // Agregar fila al lote
  const agregarFila = () => {
    setProductos(prev => [...prev, {
      categoria_id: "",
      material_id: "",
      cantidad: "",
      stock_disponible: 0
    }]);
  };

  // Quitar fila del lote
  const quitarFila = (idx) => {
    setProductos(prev => prev.filter((_, i) => i !== idx));
  };

  // Actualizar campo de una fila
  const setCampo = (i, campo, valor) => {
    const copia = [...productos];
    copia[i][campo] = valor;

    // Si cambia la categoría, limpiar el material
    if (campo === "categoria_id") {
      copia[i].material_id = "";
      copia[i].stock_disponible = 0;
    }

    // Si cambia el material, verificar stock
    if (campo === "material_id") {
      verificarStockMaterial(i, valor);
    }

    setProductos(copia);
  };

  // Verificar stock disponible de un material (CON VALIDACIÓN IGUAL QUE SALIDAS.JSX)
  const verificarStockMaterial = useCallback(async (index, materialId) => {
    if (!materialId || !almacenId) return;

    try {
      const params = {
        MaterialId: materialId,
        AlmacenId: almacenId
      };

      if (proyectoId) {
        params.ProyectoId = proyectoId;
      }

      const res = await api.get('/salidas/stock-disponible', { params });
      
      const copia = [...productos];
      copia[index].stock_disponible = res.data.disponible ? res.data.cantidad : 0;
      setProductos(copia);

    } catch (error) {
      console.error('Error al verificar stock:', error);
      const copia = [...productos];
      copia[index].stock_disponible = 0;
      setProductos(copia);
    }
  }, [almacenId, proyectoId, productos]);

  // Filtrar materiales por categoría
  const getMaterialesFiltrados = (categoriaId) => {
    if (!categoriaId) return materiales;
    return materiales.filter(m => m.CategoriaId === parseInt(categoriaId));
  };

  // Registrar lote de salidas
  const registrarLote = async () => {
    try {
      // ═══════════════════════════════════════════════════════════════════════
      // 1️⃣ VALIDACIONES BÁSICAS (IGUAL QUE SALIDAS.JSX)
      // ═══════════════════════════════════════════════════════════════════════
      
      if (!almacenId) {
        setMensaje({
          texto: "⚠️ VALIDACIÓN REQUERIDA",
          detalle: "Debe seleccionar un almacén de origen.",
          tipo: "error"
        });
        return;
      }

      if (!departamentoId) {
        setMensaje({
          texto: "⚠️ VALIDACIÓN REQUERIDA",
          detalle: "Debe seleccionar un departamento de destino.",
          tipo: "error"
        });
        return;
      }

      if (!responsable || responsable.trim() === "") {
        setMensaje({
          texto: "⚠️ VALIDACIÓN REQUERIDA",
          detalle: "El responsable es obligatorio.",
          tipo: "error"
        });
        return;
      }

      if (productos.length === 0) {
        setMensaje({
          texto: "⚠️ VALIDACIÓN REQUERIDA",
          detalle: "Agregue al menos un material al lote.",
          tipo: "error"
        });
        return;
      }

      // Filtrar productos válidos
      const itemsValidos = productos.filter(
        p => p.material_id && Number(p.cantidad) > 0
      );

      if (itemsValidos.length === 0) {
        setMensaje({
          texto: "⚠️ MATERIALES INVÁLIDOS",
          detalle: "Verifique que los materiales tengan código y cantidad válidos.",
          tipo: "error"
        });
        return;
      }

      // ═══════════════════════════════════════════════════════════════════════
      // 2️⃣ VALIDAR STOCK DE CADA MATERIAL (IGUAL QUE SALIDAS.JSX)
      // ═══════════════════════════════════════════════════════════════════════
      
      for (const item of itemsValidos) {
        const cantidad = Number(item.cantidad);
        
        if (cantidad <= 0) {
          const material = materiales.find(m => m.id === parseInt(item.material_id));
          setMensaje({
            texto: "⚠️ CANTIDAD INVÁLIDA",
            detalle: `${material?.nombre}: La cantidad debe ser mayor a cero.`,
            tipo: "error"
          });
          return;
        }

        if (item.stock_disponible === 0) {
          const material = materiales.find(m => m.id === parseInt(item.material_id));
          setMensaje({
            texto: "📦 SIN STOCK DISPONIBLE",
            detalle: `No hay existencias del material "${material?.nombre}" en el almacén seleccionado.`,
            material: material?.nombre,
            tipo: "error"
          });
          return;
        }

        if (cantidad > item.stock_disponible) {
          const material = materiales.find(m => m.id === parseInt(item.material_id));
          setMensaje({
            texto: "📊 STOCK INSUFICIENTE",
            detalle: `La cantidad solicitada (${cantidad}) excede el stock disponible.`,
            material: material?.nombre,
            cantidad_disponible: item.stock_disponible,
            cantidad_solicitada: cantidad,
            faltante: cantidad - item.stock_disponible,
            tipo: "error",
            accion_requerida: `Ajuste la cantidad a ${Math.floor(item.stock_disponible)} unidades o menos.`
          });
          return;
        }
      }

      // ═══════════════════════════════════════════════════════════════════════
      // 3️⃣ ENVIAR AL BACKEND
      // ═══════════════════════════════════════════════════════════════════════
      
      setGuardando(true);
      setMensaje({ texto: "", tipo: "" });

      const payload = {
        AlmacenId: parseInt(almacenId),
        departamento_id: parseInt(departamentoId),
        responsable: responsable.trim(),
        fecha: fecha,
        observacion: observacion.trim() || "",
        usuario_solicitante: usuario?.nombre || usuario?.email || "Usuario",
        productos: itemsValidos.map(p => ({
          MaterialId: parseInt(p.material_id),
          cantidad: Number(p.cantidad)
        }))
      };

      if (proyectoId) {
        payload.proyectoId = parseInt(proyectoId);
      }

      const res = await api.post("/salidas/lote", payload);

      setMensaje({
        texto: res.data.mensaje || "✅ LOTE REGISTRADO CORRECTAMENTE",
        detalle: res.data.detalle || `Se registraron ${itemsValidos.length} materiales. Pendiente de aprobación.`,
        tipo: "success"
      });

      // Limpiar formulario
      setProyectoId("");
      setAlmacenId("");
      setDepartamentoId("");
      setResponsable(usuario?.nombre || usuario?.email || "Usuario");
      setFecha(obtenerFechaLocal());
      setObservacion("");
      setProductos([]);

    } catch (error) {
      console.error("❌ Error al registrar lote:", error);
      const errorData = error.response?.data;

      if (errorData) {
        setMensaje({
          texto: errorData.mensaje || "❌ Error al registrar el lote",
          detalle: errorData.detalle || errorData.error,
          material: errorData.material,
          almacen: errorData.almacen,
          proyecto_actual: errorData.proyecto_actual,
          cantidad_disponible: errorData.cantidad_disponible,
          cantidad_solicitada: errorData.cantidad_solicitada,
          faltante: errorData.faltante,
          accion_requerida: errorData.accion_requerida,
          tipo: errorData.tipo || "error"
        });
      } else {
        setMensaje({
          texto: "❌ Error al registrar el lote",
          detalle: "Ocurrió un error inesperado. Por favor, intente nuevamente.",
          tipo: "error"
        });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>📦 Registrar Salida por Lote</h1>
        <p style={styles.subtitle}>Registre múltiples materiales en una sola operación</p>
      </div>

      {/* Mensaje de validación */}
      {mensaje.texto && (
        <MensajeValidacion
          mensaje={mensaje}
          onClose={() => setMensaje({ texto: "", tipo: "", detalle: "" })}
        />
      )}

      {/* Formulario Principal */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📋 Información General del Lote</h3>

        <div style={styles.formGrid}>
          {/* Proyecto */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              🏗️ Proyecto <span style={styles.optional}>(Opcional)</span>
            </label>
            <select
              style={styles.select}
              value={proyectoId}
              onChange={(e) => setProyectoId(e.target.value)}
            >
              <option value="">-- Seleccione Proyecto --</option>
              {proyectos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Almacén */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              📦 Almacén de Origen <span style={styles.required}>*</span>
            </label>
            <select
              style={styles.select}
              value={almacenId}
              onChange={(e) => setAlmacenId(e.target.value)}
              required
            >
              <option value="">-- Seleccione Almacén --</option>
              {almacenes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Departamento */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              🏢 Departamento Destino <span style={styles.required}>*</span>
            </label>
            <select
              style={styles.select}
              value={departamentoId}
              onChange={(e) => setDepartamentoId(e.target.value)}
              required
            >
              <option value="">-- Seleccione Departamento --</option>
              {departamentos.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Responsable */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              👤 Responsable <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              style={styles.input}
              value={responsable}
              onChange={(e) => setResponsable(e.target.value)}
              placeholder="Nombre del responsable"
              required
            />
          </div>

          {/* Fecha */}
          <div style={styles.formGroup}>
            <label style={styles.label}>📅 Fecha</label>
            <input
              type="date"
              style={styles.input}
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>

          {/* Observaciones */}
          <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
            <label style={styles.label}>📝 Observaciones</label>
            <textarea
              style={styles.textarea}
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              placeholder="Notas adicionales sobre este lote..."
              rows="3"
            />
          </div>
        </div>
      </div>

      {/* Tabla de Materiales */}
      <div style={styles.card}>
        <div style={styles.tableHeader}>
          <h3 style={styles.cardTitle}>📦 Materiales del Lote</h3>
          <button style={styles.btnAgregar} onClick={agregarFila}>
            ➕ Agregar Material
          </button>
        </div>

        {productos.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyIcon}>📦</p>
            <p style={styles.emptyText}>No hay materiales en el lote</p>
            <p style={styles.emptyHint}>Haga clic en "Agregar Material" para comenzar</p>
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeaderRow}>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Categoría</th>
                  <th style={styles.th}>Material</th>
                  <th style={styles.th}>Cantidad</th>
                  <th style={styles.th}>Stock Disp.</th>
                  <th style={styles.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {productos.map((p, i) => (
                  <tr key={i} style={styles.tr}>
                    <td style={styles.td}>{i + 1}</td>
                    
                    {/* Categoría */}
                    <td style={styles.td}>
                      <select
                        style={styles.selectSmall}
                        value={p.categoria_id}
                        onChange={(e) => setCampo(i, "categoria_id", e.target.value)}
                      >
                        <option value="">Todas</option>
                        {categorias.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nombre}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Material */}
                    <td style={styles.td}>
                      <select
                        style={styles.selectSmall}
                        value={p.material_id}
                        onChange={(e) => setCampo(i, "material_id", e.target.value)}
                      >
                        <option value="">-- Seleccione --</option>
                        {getMaterialesFiltrados(p.categoria_id).map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.codigo} - {m.nombre}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Cantidad */}
                    <td style={styles.td}>
                      <input
                        type="number"
                        style={styles.inputSmall}
                        value={p.cantidad}
                        onChange={(e) => setCampo(i, "cantidad", e.target.value)}
                        placeholder="0"
                        min="0"
                        step="1"
                      />
                    </td>

                    {/* Stock Disponible */}
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: p.stock_disponible > 0 ? '#d1fae5' : '#fee2e2',
                        color: p.stock_disponible > 0 ? '#065f46' : '#991b1b'
                      }}>
                        {p.stock_disponible || 0}
                      </span>
                    </td>

                    {/* Acciones */}
                    <td style={styles.td}>
                      <button
                        style={styles.btnEliminar}
                        onClick={() => quitarFila(i)}
                        title="Eliminar material"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Resumen */}
        {productos.length > 0 && (
          <div style={styles.resumen}>
            <p style={styles.resumenText}>
              <strong>Total de materiales:</strong> {productos.length}
            </p>
            <p style={styles.resumenText}>
              <strong>Materiales válidos:</strong> {productos.filter(p => p.material_id && p.cantidad > 0).length}
            </p>
          </div>
        )}
      </div>

      {/* Botones de Acción */}
      <div style={styles.actions}>
        <button
          style={styles.btnCancelar}
          onClick={() => {
            setProductos([]);
            setObservacion("");
          }}
          disabled={guardando}
        >
          ❌ Limpiar
        </button>
        <button
          style={styles.btnGuardar}
          onClick={registrarLote}
          disabled={guardando || productos.length === 0}
        >
          {guardando ? "⏳ Guardando..." : "✅ Registrar Lote"}
        </button>
      </div>
    </div>
  );
};

// ============================================
// 🎨 ESTILOS
// ============================================
const styles = {
  container: {
    padding: "20px",
    maxWidth: "1400px",
    margin: "0 auto",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  header: {
    marginBottom: "30px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e293b",
    margin: "0 0 8px 0",
  },
  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    margin: 0,
  },
  card: {
    background: "white",
    borderRadius: "12px",
    padding: "25px",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e2e8f0",
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
    margin: "0 0 20px 0",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#475569",
  },
  required: {
    color: "#ef4444",
    fontSize: "16px",
  },
  optional: {
    fontSize: "12px",
    color: "#94a3b8",
    fontWeight: "normal",
  },
  input: {
    padding: "10px 12px",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1e293b",
    outline: "none",
    transition: "border-color 0.2s",
  },
  select: {
    padding: "10px 12px",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1e293b",
    outline: "none",
    backgroundColor: "white",
    cursor: "pointer",
    transition: "border-color 0.2s",
  },
  textarea: {
    padding: "10px 12px",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#1e293b",
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    transition: "border-color 0.2s",
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  btnAgregar: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "transform 0.2s",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#94a3b8",
  },
  emptyIcon: {
    fontSize: "48px",
    margin: "0 0 10px 0",
  },
  emptyText: {
    fontSize: "16px",
    fontWeight: "600",
    margin: "0 0 5px 0",
  },
  emptyHint: {
    fontSize: "14px",
    margin: 0,
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  },
  tableHeaderRow: {
    background: "#1e293b",
    color: "white",
  },
  th: {
    padding: "12px 8px",
    textAlign: "left",
    fontWeight: "600",
  },
  tr: {
    borderBottom: "1px solid #e2e8f0",
  },
  td: {
    padding: "10px 8px",
    color: "#475569",
  },
  selectSmall: {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "13px",
    outline: "none",
  },
  inputSmall: {
    width: "100px",
    padding: "6px 8px",
    border: "1px solid #e2e8f0",
    borderRadius: "6px",
    fontSize: "13px",
    outline: "none",
  },
  badge: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },
  btnEliminar: {
    padding: "6px 10px",
    background: "#fee2e2",
    color: "#991b1b",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  resumen: {
    marginTop: "20px",
    padding: "15px",
    background: "#f8fafc",
    borderRadius: "8px",
    display: "flex",
    gap: "30px",
  },
  resumenText: {
    margin: 0,
    fontSize: "14px",
    color: "#475569",
  },
  actions: {
    display: "flex",
    gap: "15px",
    justifyContent: "flex-end",
  },
  btnCancelar: {
    padding: "12px 24px",
    background: "white",
    color: "#64748b",
    border: "2px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  btnGuardar: {
    padding: "12px 24px",
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
    transition: "all 0.2s",
  },
};

export default SalidaLote;